import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import { TaskService } from "../services/taskService";
import { useTaskActions, useTaskUpdate } from "../hooks/useTasks";
import { defaultNotificationService, TaskNotificationMessages } from "../utils/notifications";
/**
 * Convert ISO date string to YYYY-MM-DD format for date input
 */
function isoToDateInput(isoString) {
    if (!isoString)
        return "";
    try {
        const date = new Date(isoString);
        if (isNaN(date.getTime()))
            return "";
        return date.toISOString().split("T")[0];
    }
    catch {
        return "";
    }
}
/**
 * Convert YYYY-MM-DD date input to ISO string (start of day in UTC)
 */
function dateInputToIso(dateString) {
    if (!dateString)
        return undefined;
    try {
        // Create date at start of day in local timezone, then convert to ISO
        const date = new Date(dateString + "T00:00:00");
        if (isNaN(date.getTime()))
            return undefined;
        return date.toISOString();
    }
    catch {
        return undefined;
    }
}
export function TaskForm({ workspaceId, projectId, taskId, initialData, workspaceMembers = [], onSuccess, onCancel, notificationService = defaultNotificationService, }) {
    const [formData, setFormData] = useState({
        title: "",
        description: "",
        priority: "medium",
        dueDate: "",
        amount: undefined,
        transactionType: "",
        assignedTo: "",
    });
    const [errors, setErrors] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Use appropriate hooks based on whether we're creating or updating
    const { createTask, isCreating } = useTaskActions(workspaceId, projectId);
    const updateHook = taskId ? useTaskUpdate(taskId, workspaceId, projectId) : null;
    const isLoading = isSubmitting || isCreating || (updateHook?.isUpdating ?? false);
    useEffect(() => {
        if (initialData) {
            setFormData((prev) => ({
                ...prev,
                ...initialData,
                // Convert ISO date to YYYY-MM-DD format for the date input
                dueDate: isoToDateInput(initialData.dueDate),
            }));
        }
    }, [initialData]);
    const handleInputChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
        // Clear errors when user starts typing
        if (errors.length > 0) {
            setErrors([]);
        }
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        // Validate form
        const validation = TaskService.validateTaskForm({
            title: formData.title,
            dueDate: formData.dueDate,
            amount: formData.amount,
        });
        if (!validation.isValid) {
            setErrors(validation.errors);
            notificationService.error(TaskNotificationMessages.VALIDATION_FAILED);
            return;
        }
        setIsSubmitting(true);
        try {
            // Clean up form data
            const cleanedData = {
                title: formData.title.trim(),
                description: formData.description?.trim() || undefined,
                priority: formData.priority,
                dueDate: dateInputToIso(formData.dueDate),
                amount: formData.amount || undefined,
                transactionType: formData.transactionType?.trim() || undefined,
                assignedTo: formData.assignedTo || undefined,
            };
            if (taskId && updateHook) {
                // Update existing task
                const updateData = cleanedData;
                const result = await updateHook.updateTask(updateData);
                if (result.success) {
                    notificationService.success(TaskNotificationMessages.TASK_UPDATED);
                    if (onSuccess) {
                        onSuccess();
                    }
                }
                else {
                    setErrors([result.error || TaskNotificationMessages.TASK_UPDATE_FAILED]);
                    notificationService.error(result.error || TaskNotificationMessages.TASK_UPDATE_FAILED);
                }
            }
            else {
                // Create new task
                const createData = {
                    ...cleanedData,
                    projectId,
                };
                const result = await createTask(createData);
                if (result.success) {
                    notificationService.success(TaskNotificationMessages.TASK_CREATED);
                    if (onSuccess) {
                        onSuccess();
                    }
                }
                else {
                    setErrors([result.error || TaskNotificationMessages.TASK_CREATE_FAILED]);
                    notificationService.error(result.error || TaskNotificationMessages.TASK_CREATE_FAILED);
                }
            }
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred";
            setErrors([errorMessage]);
            notificationService.error(errorMessage);
        }
        finally {
            setIsSubmitting(false);
        }
    };
    return (_jsxs("form", { onSubmit: handleSubmit, className: "space-y-6", children: [errors.length > 0 && (_jsx("div", { className: "rounded-lg border border-red-200 bg-red-50 p-4", children: _jsxs("div", { className: "flex", children: [_jsx("div", { className: "flex-shrink-0", children: _jsx("svg", { className: "h-5 w-5 text-red-400", viewBox: "0 0 20 20", fill: "currentColor", children: _jsx("path", { fillRule: "evenodd", d: "M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z", clipRule: "evenodd" }) }) }), _jsxs("div", { className: "ml-3", children: [_jsx("h3", { className: "text-sm font-medium text-red-800", children: "Please fix the following errors:" }), _jsx("ul", { className: "mt-2 list-inside list-disc text-sm text-red-700", children: errors.map((error, index) => (_jsx("li", { children: error }, index))) })] })] }) })), _jsxs("div", { children: [_jsx("label", { htmlFor: "title", className: "mb-1 block text-sm font-medium text-gray-700", children: "Title *" }), _jsx("input", { type: "text", id: "title", value: formData.title, onChange: (e) => handleInputChange("title", e.target.value), className: "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500", placeholder: "Enter task title", maxLength: 200, required: true })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "description", className: "mb-1 block text-sm font-medium text-gray-700", children: "Description" }), _jsx("textarea", { id: "description", value: formData.description, onChange: (e) => handleInputChange("description", e.target.value), rows: 3, className: "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500", placeholder: "Enter task description", maxLength: 2000 })] }), _jsxs("div", { className: "grid grid-cols-1 gap-4 md:grid-cols-2", children: [_jsxs("div", { children: [_jsx("label", { htmlFor: "priority", className: "mb-1 block text-sm font-medium text-gray-700", children: "Priority" }), _jsxs("select", { id: "priority", value: formData.priority, onChange: (e) => handleInputChange("priority", e.target.value), className: "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500", children: [_jsx("option", { value: "low", children: "Low" }), _jsx("option", { value: "medium", children: "Medium" }), _jsx("option", { value: "high", children: "High" })] })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "dueDate", className: "mb-1 block text-sm font-medium text-gray-700", children: "Due Date" }), _jsx("input", { type: "date", id: "dueDate", value: formData.dueDate, onChange: (e) => handleInputChange("dueDate", e.target.value), className: "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500" })] })] }), _jsxs("div", { className: "grid grid-cols-1 gap-4 md:grid-cols-2", children: [_jsxs("div", { children: [_jsx("label", { htmlFor: "amount", className: "mb-1 block text-sm font-medium text-gray-700", children: "Amount ($)" }), _jsx("input", { type: "number", id: "amount", value: formData.amount || "", onChange: (e) => handleInputChange("amount", e.target.value ? parseFloat(e.target.value) : undefined), min: "0", step: "0.01", className: "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500", placeholder: "0.00" })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "transactionType", className: "mb-1 block text-sm font-medium text-gray-700", children: "Transaction Type" }), _jsx("input", { type: "text", id: "transactionType", value: formData.transactionType, onChange: (e) => handleInputChange("transactionType", e.target.value), className: "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500", placeholder: "e.g., Material, Labor, Equipment", maxLength: 50 })] })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "assignedTo", className: "mb-1 block text-sm font-medium text-gray-700", children: "Assign To" }), _jsxs("select", { id: "assignedTo", value: formData.assignedTo, onChange: (e) => handleInputChange("assignedTo", e.target.value), className: "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500", children: [_jsx("option", { value: "", children: "Unassigned" }), workspaceMembers
                                .filter((member) => member.status === "active")
                                .map((member) => (_jsxs("option", { value: member.userId, children: [member.user.name || member.user.phoneNumber, " (", member.role, ")"] }, member.userId)))] })] }), _jsxs("div", { className: "flex items-center justify-end space-x-3 border-t border-gray-200 pt-4", children: [_jsx("button", { type: "button", onClick: onCancel, disabled: isLoading, className: "rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50", children: "Cancel" }), _jsx("button", { type: "submit", disabled: isLoading, className: "rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50", children: isLoading ? (_jsxs("div", { className: "flex items-center", children: [_jsx("div", { className: "mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white" }), "Saving..."] })) : taskId ? ("Update Task") : ("Create Task") })] })] }));
}
